/**
 * `/casos/novo` na área do perito — a abertura v6.7 (E144.F3/F4, GUS-135 + GUS-105; antes E126.F2,
 * ADR-0088): Enviar documentos → Conferir → Acompanhar, a mesma do portal. "Mais detalhes" saiu
 * (E163.F1, ADR-0112 §4): data do ato, solicitante e espécime ficam na edição do caso. No
 * Acompanhar, "Abrir caso" leva ao destino de cada caso e "Novo caso" a uma abertura em branco.
 *
 * Sem `?caso=`, a tela Enviar documentos antes de o caso existir: o caso nasce ao escolher os
 * primeiros arquivos e a URL ganha `?caso=<id>&passo=` (com `replace`, para o "Voltar" do navegador
 * não devolver à tela em branco, de onde se abriria um segundo caso — E133.F2). A grafotécnica abre
 * o caso e segue o cadastro de documentos de sempre (`/casos/:id/wizard`). `?processo=` abre mais um
 * exame no processo existente (e `?servico=` o pré-escolhe).
 */
import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AberturaFrame } from '@/components/Abertura/AberturaFrame';
import { EnviarSemCaso } from '@/components/Abertura/EnviarSemCaso';
import { JornadaDaAbertura } from '@/components/Abertura/JornadaDaAbertura';
import { useProcessoDaAbertura } from '@/components/Abertura/useProcessoDaAbertura';
import { ptBR } from '@/i18n/pt-BR';
import { comACustodia, passoDaUrl, VISTA_DA_CUSTODIA } from '@/lib/abertura';
import { useApiClient } from '@/lib/api';
import { useCurrentRole } from '@/lib/auth/useCurrentRole';
import type { CaseRecordData } from '@/lib/cases';

export function NovoCasoPage(): JSX.Element {
  const client = useApiClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const caseId = searchParams.get('caso');
  const paraOCadastro = useCallback(
    (caso: CaseRecordData) => navigate(`/casos/${caso.id}/wizard`, { replace: true }),
    [navigate],
  );
  if (caseId !== null) {
    return (
      <JornadaDaAbertura
        key={caseId}
        client={client}
        caseId={caseId}
        passo={passoDaUrl(searchParams.get('passo'))}
        onPasso={(passo) => setSearchParams({ caso: caseId, passo }, { replace: true })}
        custodia={searchParams.get('vista') === VISTA_DA_CUSTODIA}
        onCustodia={(aberta) => setSearchParams(comACustodia(searchParams, aberta))}
        onAbrirCaso={(id) => navigate(`/casos/${id}`)}
        onNovoCaso={() => navigate('/casos/novo')}
        onSair={() => navigate('/casos')}
        onOutroServico={paraOCadastro}
      />
    );
  }
  return (
    <SemCaso
      processoId={searchParams.get('processo')}
      servico={searchParams.get('servico')}
      onAberto={(caso) =>
        caso.modalidade === 'eletronica'
          ? setSearchParams({ caso: caso.id, passo: 'contratos' }, { replace: true })
          : paraOCadastro(caso)
      }
      onVoltar={() => navigate('/casos')}
    />
  );
}

function SemCaso({
  processoId,
  servico,
  onAberto,
  onVoltar,
}: {
  processoId: string | null;
  servico: string | null;
  onAberto: (caso: CaseRecordData) => void;
  onVoltar: () => void;
}): JSX.Element {
  const client = useApiClient();
  const { processo, erro, servicoInicial } = useProcessoDaAbertura(client, processoId, servico);
  // E177.F1 (ADR-0131): a operação abre com a origem do caso (o banco e o escritório requerente).
  const { isPlatform } = useCurrentRole();
  return (
    <AberturaFrame passo="enviar">
      {erro ? (
        <p role="alert" className="text-body-sm text-danger" data-testid="abertura-processo-erro">
          {ptBR.abertura.processo.erro}
        </p>
      ) : processo === null ? (
        <p className="text-body-sm text-fg-muted">{ptBR.abertura.processo.carregando}</p>
      ) : (
        <EnviarSemCaso
          key={processo?.id ?? 'novo'}
          client={client}
          {...(processo !== undefined ? { processo } : {})}
          {...(servicoInicial !== undefined ? { servicoInicial } : {})}
          daOperacao={isPlatform}
          onAberto={onAberto}
          onVoltar={onVoltar}
        />
      )}
    </AberturaFrame>
  );
}
